import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Clock, Loader2, Video, ArrowLeft, Send, NotebookPen } from "lucide-react";
import { VoiceRecorderButton, VoiceMessagePlayer } from "../components/VoiceMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VideoVisit() {
  const { id } = useParams();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recap, setRecap] = useState("");
  const [recapSending, setRecapSending] = useState(false);
  const [recapDone, setRecapDone] = useState(false);
  const scrollRef = useRef(null);

  const adminToken = localStorage.getItem("soarlife-admin-token");
  const clientToken = localStorage.getItem("soarlife-client-token");
  const bearer = adminToken || clientToken;
  const reqConfig = () => ({
    headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
    params: token ? { token } : {},
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/video-visit/${id}`, reqConfig());
        setInfo(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "We couldn't open this video visit.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/video-visit/${id}/messages`, reqConfig());
      setMessages(res.data);
    } catch {}
  };

  useEffect(() => {
    if (!info) return;
    loadMessages();
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/video-visit/${id}/messages`, { content }, reqConfig());
      setInput("");
      await loadMessages();
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob, ext, duration) => {
    const fd = new FormData();
    fd.append("file", blob, `voice.${ext}`);
    fd.append("duration", String(duration));
    await axios.post(`${API}/video-visit/${id}/messages/voice`, fd, {
      headers: { ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}), "Content-Type": "multipart/form-data" },
      params: token ? { token } : {},
    });
    await loadMessages();
  };

  const sendRecap = async (e) => {
    e?.preventDefault();
    if (!recap.trim() || recapSending) return;
    setRecapSending(true);
    try {
      await axios.post(
        `${API}/video-visit/${id}/recap`,
        { content: recap.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setRecap("");
      setRecapDone(true);
      await loadMessages();
      setTimeout(() => setRecapDone(false), 4000);
    } finally {
      setRecapSending(false);
    }
  };

  const isAdmin = info?.viewer_role === "admin";
  const mine = (m) => (isAdmin ? m.author_role === "admin" : m.author_role === "client");

  return (
    <div className="min-h-screen bg-[#3F5043]" data-testid="video-visit-page">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-[#AE8F78] text-xs uppercase tracking-[0.2em] hover:text-[#F8F7F3] transition-colors mb-6">
          <ArrowLeft size={14} /> Soarlife
        </Link>

        {error && (
          <div className="bg-[#F8F7F3] rounded-3xl p-8 text-center" data-testid="video-error">
            <p className="text-[#875F45] text-sm">{error}</p>
            <Link to="/book" className="inline-block mt-4 text-xs uppercase tracking-[0.2em] text-[#3F5043] underline">
              Book a consultation
            </Link>
          </div>
        )}

        {!info && !error && (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-[#AE8F78]" />
          </div>
        )}

        {info && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h1 className="font-serif italic text-3xl sm:text-4xl text-[#F8F7F3]" data-testid="video-title">
                  {info.title}
                </h1>
                <div className="flex items-center gap-4 text-[#F8F7F3]/70 text-sm mt-1">
                  <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {info.date}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {info.time} ET</span>
                </div>
              </div>
              {!joined && (
                <button
                  onClick={() => setJoined(true)}
                  data-testid="video-join-btn"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#D4AF37] text-[#3F5043] text-sm font-semibold hover:bg-[#F8F7F3] transition-colors"
                >
                  <Video size={16} /> Join video visit
                </button>
              )}
            </div>

            <div className={`grid gap-5 ${joined ? "lg:grid-cols-3" : ""}`}>
              <div className={joined ? "lg:col-span-2" : ""}>
                {joined ? (
                  <div className="rounded-3xl overflow-hidden border border-[#AE8F78]/30 shadow-2xl" data-testid="video-room">
                    <iframe
                      src={`https://meet.jit.si/${info.room}`}
                      title="Soarlife video visit"
                      allow="camera; microphone; fullscreen; display-capture; autoplay"
                      className="w-full h-[72vh] bg-black"
                    />
                  </div>
                ) : (
                  <div className="bg-[#F8F7F3]/5 border border-[#AE8F78]/25 rounded-3xl p-8 text-center" data-testid="video-lobby">
                    <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
                      <Video size={24} strokeWidth={1.5} />
                    </div>
                    <p className="text-[#F8F7F3]/85 text-sm max-w-md mx-auto leading-relaxed">
                      This is your private Soarlife video room. Join a few minutes before your appointment —
                      allow camera and microphone access when your browser asks.
                    </p>
                    <p className="text-[#F8F7F3]/50 text-xs mt-3">
                      Trouble connecting? Call your care team at (407) 374-9834.
                    </p>
                  </div>
                )}
              </div>

              {joined && (
                <div className="bg-[#F8F7F3] rounded-3xl overflow-hidden flex flex-col" data-testid="visit-chat">
                  <div className="px-5 py-4 border-b border-[#BCB3A6]/40">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#899480] font-semibold">Visit messages</div>
                    <div className="text-xs text-[#3F5043]/60 mt-0.5">Text and voice notes with your care team during the visit</div>
                  </div>
                  <div ref={scrollRef} className="flex-1 h-64 lg:h-auto overflow-y-auto p-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center text-[#899480] text-xs py-8">
                        No messages yet — say hello.
                      </div>
                    ) : (
                      messages.map((m) => (
                        <div key={m.id} className={`flex ${mine(m) ? "justify-end" : "justify-start"}`}>
                          <div className="max-w-[85%]">
                            <div className={`text-[9px] uppercase tracking-[0.14em] font-semibold mb-0.5 ${mine(m) ? "text-right text-[#899480]" : "text-[#875F45]"}`}>
                              {m.author_name}
                            </div>
                            {m.kind === "voice" ? (
                              <VoiceMessagePlayer
                                messageId={m.id}
                                duration={m.duration}
                                mine={mine(m)}
                                audioUrl={`${API}/video-visit/${id}/voice/${m.id}/audio`}
                                requestParams={token ? { token } : {}}
                                authHeader={bearer ? () => ({ Authorization: `Bearer ${bearer}` }) : null}
                              />
                            ) : (
                              <div
                                data-testid={m.kind === "recap" ? `visit-recap-${m.id}` : undefined}
                                className={`px-3.5 py-2 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap ${
                                  m.kind === "recap"
                                    ? "bg-[#D4AF37]/15 text-[#3F5043] border border-[#D4AF37]/50"
                                    : mine(m)
                                      ? "bg-[#3F5043] text-[#F8F7F3] rounded-br-sm"
                                      : "bg-[#875F45]/15 text-[#3F5043] border border-[#875F45]/30 rounded-bl-sm"
                                }`}
                              >
                                {m.kind === "recap" && (
                                  <span className="block text-[9px] uppercase tracking-[0.18em] font-bold text-[#875F45] mb-1">
                                    Visit recap
                                  </span>
                                )}
                                {m.content}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <form onSubmit={send} className="border-t border-[#BCB3A6]/40 p-3 flex items-end gap-2">
                    <input
                      data-testid="visit-chat-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Type a message…"
                      className="flex-1 bg-[#EDEDEE] rounded-2xl px-4 py-2.5 text-xs text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480]"
                    />
                    <VoiceRecorderButton onSend={sendVoice} disabled={sending} testid="visit-voice-btn" />
                    <button
                      type="submit"
                      data-testid="visit-chat-send"
                      disabled={sending || !input.trim()}
                      aria-label="Send message"
                      className="w-11 h-11 rounded-full bg-[#3F5043] text-[#F8F7F3] flex items-center justify-center hover:bg-[#875F45] transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="mt-6 bg-[#F8F7F3] rounded-3xl p-6" data-testid="recap-composer">
                <div className="flex items-center gap-2 mb-3">
                  <NotebookPen size={16} className="text-[#875F45]" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#899480] font-semibold">
                    Visit recap — sent to the client's chat when the visit ends
                  </div>
                </div>
                <form onSubmit={sendRecap}>
                  <textarea
                    data-testid="recap-input"
                    rows={3}
                    value={recap}
                    onChange={(e) => setRecap(e.target.value)}
                    placeholder="Summarize what was discussed, next steps, and anything the client should remember…"
                    className="w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]"
                  />
                  <div className="flex items-center gap-3 mt-3">
                    <button
                      type="submit"
                      data-testid="recap-send-btn"
                      disabled={recapSending || !recap.trim()}
                      className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-50"
                    >
                      {recapSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                      Send recap to client
                    </button>
                    {recapDone && (
                      <span className="text-xs text-[#899480]" data-testid="recap-sent-msg">
                        Recap delivered to the visit chat and the client's portal messages.
                      </span>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
